import { Document, Page, Text, View, StyleSheet, Image, Link, pdf } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    paddingTop: 48,
    paddingBottom: 48,
    paddingHorizontal: 56,
    color: "#1a1a1a",
  },

  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  businessBlock: {
    maxWidth: "55%",
  },
  logoImage: {
    maxHeight: 48,
    maxWidth: 160,
    objectFit: "contain",
    marginBottom: 6,
  },
  businessName: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#16a34a",
    marginBottom: 4,
  },
  businessDetail: {
    fontSize: 9,
    color: "#555",
    lineHeight: 1.5,
  },
  invoiceMeta: {
    alignItems: "flex-end",
  },
  invoiceTitle: {
    fontSize: 26,
    fontFamily: "Helvetica-Bold",
    color: "#16a34a",
    marginBottom: 6,
  },
  metaRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  metaLabel: {
    fontSize: 9,
    color: "#888",
    width: 64,
    textAlign: "right",
    marginRight: 6,
  },
  metaValue: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    color: "#1a1a1a",
  },

  // Divider
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    marginBottom: 20,
  },

  // Bill To
  billSection: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 6,
  },
  clientName: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  clientDetail: {
    fontSize: 9,
    color: "#555",
    lineHeight: 1.5,
  },

  // Status badge
  statusRow: {
    flexDirection: "row",
    marginBottom: 20,
  },
  badgePaid: {
    backgroundColor: "#dcfce7",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgePaidText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#16a34a",
  },
  badgeUnpaid: {
    backgroundColor: "#fef9c3",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeUnpaidText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#a16207",
  },

  // Line items table
  table: {
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableRowAlt: {
    backgroundColor: "#f9fafb",
  },
  colName: { width: 100 },
  colDesc: { flex: 1 },
  colQty: { width: 48, textAlign: "right" },
  colPrice: { width: 72, textAlign: "right" },
  colTotal: { width: 72, textAlign: "right" },
  tableHeaderText: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#6b7280",
    textTransform: "uppercase",
  },
  tableBodyText: {
    fontSize: 9,
    color: "#1a1a1a",
  },

  // Totals
  totalsSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 24,
  },
  totalsBox: {
    width: 200,
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 3,
  },
  totalsLabel: {
    fontSize: 9,
    color: "#555",
  },
  totalsValue: {
    fontSize: 9,
    color: "#1a1a1a",
  },
  totalsDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    marginVertical: 4,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  totalLabel: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },
  totalValue: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#16a34a",
  },

  // Notes
  notesSection: {
    marginBottom: 24,
  },
  notesText: {
    fontSize: 9,
    color: "#555",
    lineHeight: 1.6,
    borderLeftWidth: 2,
    borderLeftColor: "#d1d5db",
    paddingLeft: 8,
  },

  // Pay online
  paySection: {
    marginBottom: 24,
    alignItems: "flex-start",
  },
  payButton: {
    backgroundColor: "#16a34a",
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    alignSelf: "flex-start",
  },
  payButtonText: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#ffffff",
  },
  paySubtext: {
    fontSize: 8,
    color: "#6b7280",
    marginTop: 4,
  },

  // Footer
  footer: {
    position: "absolute",
    bottom: 32,
    left: 56,
    right: 56,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 8,
    color: "#9ca3af",
  },
});

const fmt = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

const InvoicePDFDocument = ({ invoice }) => {
  const taxRate = parseFloat(invoice.tax_rate) || 0;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.businessBlock}>
            {invoice.business_logo_url ? <Image src={invoice.business_logo_url} style={styles.logoImage} /> : null}
            <Text style={styles.businessName}>{invoice.business_name || "Your Business"}</Text>
            {invoice.business_address ? <Text style={styles.businessDetail}>{invoice.business_address}</Text> : null}
            {(invoice.business_city || invoice.business_state) ? (
              <Text style={styles.businessDetail}>
                {invoice.business_city}{invoice.business_city && invoice.business_state ? ", " : ""}{invoice.business_state} {invoice.business_zip}
              </Text>
            ) : null}
            {invoice.business_phone ? <Text style={styles.businessDetail}>{invoice.business_phone}</Text> : null}
            {invoice.business_email ? <Text style={styles.businessDetail}>{invoice.business_email}</Text> : null}
          </View>

          <View style={styles.invoiceMeta}>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Number</Text>
              <Text style={styles.metaValue}>#{invoice.invoice_number}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Date</Text>
              <Text style={styles.metaValue}>{invoice.date}</Text>
            </View>
            {invoice.due_date ? (
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Due</Text>
                <Text style={styles.metaValue}>{invoice.due_date}</Text>
              </View>
            ) : null}
          </View>
        </View>

        <View style={styles.divider} />

        {/* Bill To + Status */}
        <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 24 }}>
          <View style={styles.billSection}>
            <Text style={styles.sectionLabel}>Bill To</Text>
            <Text style={styles.clientName}>{invoice.client_name}</Text>
            {invoice.client_address ? <Text style={styles.clientDetail}>{invoice.client_address}</Text> : null}
            {(invoice.client_city || invoice.client_state || invoice.client_zip) ? (
              <Text style={styles.clientDetail}>
                {[invoice.client_city, invoice.client_state].filter(Boolean).join(", ")}{invoice.client_zip ? ` ${invoice.client_zip}` : ""}
              </Text>
            ) : null}
            {invoice.client_country ? <Text style={styles.clientDetail}>{invoice.client_country}</Text> : null}
            {invoice.client_email ? <Text style={styles.clientDetail}>{invoice.client_email}</Text> : null}
            {invoice.client_phone ? <Text style={styles.clientDetail}>{invoice.client_phone}</Text> : null}
          </View>

          <View>
            {invoice.paid ? (
              <View style={styles.badgePaid}>
                <Text style={styles.badgePaidText}>PAID{invoice.paid_on ? ` · ${invoice.paid_on}` : ""}</Text>
              </View>
            ) : (
              <View style={styles.badgeUnpaid}>
                <Text style={styles.badgeUnpaidText}>UNPAID</Text>
              </View>
            )}
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.colName]}>Item</Text>
            <Text style={[styles.tableHeaderText, styles.colDesc]}>Description</Text>
            <Text style={[styles.tableHeaderText, styles.colQty]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.colPrice]}>Unit Price</Text>
            <Text style={[styles.tableHeaderText, styles.colTotal]}>Total</Text>
          </View>
          {(invoice.line_items || []).map((item, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 1 ? styles.tableRowAlt : {}]}>
              <Text style={[styles.tableBodyText, styles.colName, { fontFamily: "Helvetica-Bold" }]}>{item.name || ""}</Text>
              <Text style={[styles.tableBodyText, styles.colDesc, { color: "#555" }]}>{item.description || ""}</Text>
              <Text style={[styles.tableBodyText, styles.colQty]}>{item.quantity}</Text>
              <Text style={[styles.tableBodyText, styles.colPrice]}>{fmt(item.unit_price)}</Text>
              <Text style={[styles.tableBodyText, styles.colTotal]}>{fmt(item.total)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Subtotal</Text>
              <Text style={styles.totalsValue}>{fmt(invoice.subtotal)}</Text>
            </View>
            {taxRate > 0 && (
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Tax ({taxRate}%)</Text>
                <Text style={styles.totalsValue}>{fmt(invoice.tax_amount)}</Text>
              </View>
            )}
            <View style={styles.totalsDivider} />
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Due</Text>
              <Text style={styles.totalValue}>{fmt(invoice.total_amount)}</Text>
            </View>
          </View>
        </View>

        {/* Pay Online — only shown when invoice is unpaid and payment_url is set */}
        {!invoice.paid && invoice.payment_url ? (
          <View style={styles.paySection}>
            <Text style={[styles.sectionLabel, { marginBottom: 8 }]}>Pay Online</Text>
            <Link src={invoice.payment_url}>
              <View style={styles.payButton}>
                <Text style={styles.payButtonText}>Pay Now →</Text>
              </View>
            </Link>
            <Text style={styles.paySubtext}>Secure payment powered by Stripe</Text>
            <Text style={styles.paySubtext}>
              A processing fee applies to online payments: 2.9% + $0.30 (Stripe card fee) + 1% (PayClear service fee).
            </Text>
          </View>
        ) : null}

        {/* Notes */}
        {invoice.notes ? (
          <View style={styles.notesSection}>
            <Text style={styles.sectionLabel}>Notes</Text>
            <Text style={styles.notesText}>{invoice.notes}</Text>
          </View>
        ) : null}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Invoice #{invoice.invoice_number} · {invoice.business_name || ""}</Text>
          <Text style={styles.footerText}>Generated by PayClear</Text>
        </View>

      </Page>
    </Document>
  );
};

export const getInvoicePDFBase64 = async (invoice) => {
  const blob = await pdf(<InvoicePDFDocument invoice={invoice} />).toBlob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const downloadInvoicePDF = async (invoice) => {
  const blob = await pdf(<InvoicePDFDocument invoice={invoice} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Invoice-${invoice.invoice_number}-${invoice.client_name.replace(/\s+/g, "_")}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

export default InvoicePDFDocument;
